/**
 * SessionsSection — afișează sesiunile active ale user-ului curent și permite
 * revocare individuală sau bulk (toate except curentă).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { authApi, type SessionInfo } from '@/api/auth';
import { ApiError } from '@/api/client';
import { formatDateTime, formatRelative } from '@/lib/format';
import { confirmDialog } from '@/stores/dialog';

export default function SessionsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => authApi.listSessions(),
  });

  const revokeOne = useMutation({
    mutationFn: (id: string) => authApi.revokeSession(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const revokeAll = useMutation({
    mutationFn: () => authApi.revokeAllOtherSessions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const others = (sessions.data ?? []).filter((s) => !s.is_current);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-[var(--text)]">
            {t('settings.sessions_title')}
          </div>
          <div className="text-xs text-[var(--text-dim)]">
            {t('security_section.no_recognize')}
          </div>
        </div>
        {others.length > 0 && (
          <button
            type="button"
            className="btn-outline text-xs"
            onClick={async () => {
              if (
                await confirmDialog(
                  t('security_section.session_revoke_all_confirm', {
                    count: others.length,
                  }),
                )
              ) {
                revokeAll.mutate();
              }
            }}
            disabled={revokeAll.isPending}
          >
            {revokeAll.isPending
              ? t('security_section.session_revoking')
              : t('settings.revoke_others', { count: others.length })}
          </button>
        )}
      </div>

      {sessions.isLoading && (
        <p className="text-xs text-[var(--text-muted)] mt-2">{t('common.loading')}</p>
      )}

      {sessions.isError && (
        <div
          className="text-xs rounded px-3 py-2 mt-2"
          style={{
            color: 'var(--danger)',
            background: 'var(--danger-bg)',
            border: '1px solid var(--danger)',
          }}
        >
          {t('common.error')}:{' '}
          {sessions.error instanceof ApiError
            ? sessions.error.detail
            : String(sessions.error)}
        </div>
      )}

      {sessions.data && sessions.data.length > 0 && (
        <div className="mt-3 space-y-2">
          {sessions.data.map((s) => (
            <SessionRow
              key={s.id}
              session={s}
              busy={revokeOne.isPending}
              onRevoke={async () => {
                if (await confirmDialog(t('security_section.session_revoke_confirm'))) revokeOne.mutate(s.id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SessionRow({
  session,
  busy,
  onRevoke,
}: {
  session: SessionInfo;
  busy: boolean;
  onRevoke: () => void;
}) {
  const { t } = useTranslation();
  const { device, browser } = parseUserAgent(
    session.user_agent,
    t('admin_ui.sessions.unknown_device'),
  );

  return (
    <div
      className="flex items-start gap-3 rounded px-3 py-2"
      style={{
        background: session.is_current ? 'var(--accent-bg)' : 'var(--bg-surface)',
        border: session.is_current
          ? '1px solid var(--accent)'
          : '1px solid var(--border)',
      }}
    >
      <div
        className="grid place-items-center w-8 h-8 rounded-full flex-shrink-0"
        style={{
          background: session.is_current ? 'var(--accent)' : 'var(--bg-elevated)',
          color: session.is_current ? 'var(--bg)' : 'var(--text-muted)',
          border: session.is_current ? 'none' : '1px solid var(--border)',
        }}
        aria-hidden
      >
        <DeviceIcon />
      </div>
      <div className="flex-1 min-w-0 text-sm text-[var(--text)]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{device}</span>
          <span className="text-[var(--text-dim)] text-xs">{browser}</span>
          {session.is_current && (
            <span
              className="badge text-[10px] uppercase"
              style={{
                background: 'var(--accent)',
                color: 'var(--bg)',
                border: 'none',
                letterSpacing: '0.08em',
              }}
            >
              {t('settings.current_session').toLowerCase()}
            </span>
          )}
        </div>
        <div
          className="text-[11px] mt-0.5 font-mono flex items-center gap-2 flex-wrap"
          style={{ color: 'var(--text-dim)' }}
        >
          <span>{session.ip ?? '—'}</span>
          <span>·</span>
          <span title={formatDateTime(session.last_active_at)}>
            {t('security_section.session_logout_active', { when: formatRelative(session.last_active_at) })}
          </span>
          <span>·</span>
          <span title={formatDateTime(session.expires_at)}>
            {t('security_section.session_expires', { when: formatRelative(session.expires_at) })}
          </span>
        </div>
      </div>
      {!session.is_current && (
        <button
          type="button"
          className="text-xs hover:underline disabled:opacity-50"
          style={{ color: 'var(--danger)' }}
          onClick={onRevoke}
          disabled={busy}
        >
          {t('common.revoke').toLowerCase()}
        </button>
      )}
    </div>
  );
}

function DeviceIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M2 20h20" />
      <path d="M12 16v4" />
    </svg>
  );
}

// ============================================================
// Parse user-agent — heuristic suficient pt recunoaștere device
// ============================================================

function parseUserAgent(
  ua: string | null,
  unknownDevice: string,
): { device: string; browser: string } {
  if (!ua) return { device: unknownDevice, browser: '' };

  const browser = (() => {
    if (/Edg\//i.test(ua)) return 'Edge';
    if (/Firefox\//i.test(ua)) return 'Firefox';
    if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) return 'Chrome';
    if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) return 'Safari';
    if (/Chromium\//i.test(ua)) return 'Chromium';
    return 'browser';
  })();

  const device = (() => {
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Android/i.test(ua)) return 'Android';
    if (/Mac OS X/i.test(ua)) return 'macOS';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Linux/i.test(ua)) return 'Linux';
    return 'device';
  })();

  return { device, browser };
}
